import React from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { usePractice } from '../contexts/PracticeContext';
import PracticeDetails from '../components/PracticeDetails';
import Transactions from './Transactions';
import UsersTabContent from '../components/OrganizationUsers/UsersTabContent';
import InvitationsTabContent from '../components/OrganizationUsers/InvitationsTabContent';
import OrganizationWorkspaceHeader from '../components/OrganizationWorkspaceHeader';
import OrganizationEmptyState from '../components/OrganizationEmptyState';

function TransactionsTabContent() {
  return (
    <Box
      sx={{
        '& .MuiContainer-root': {
          mt: 0,
          '& h4:first-of-type': { display: 'none' },
        },
      }}
    >
      <Transactions />
    </Box>
  );
}

function UsersTabContentWrapper() {
  const { selectedPractice } = usePractice();

  if (!selectedPractice) {
    return (
      <Container disableGutters>
        <Typography variant="body1">
          Please select an organization to view its users
        </Typography>
      </Container>
    );
  }

  return <UsersTabContent organizationId={selectedPractice.id} organization={selectedPractice} />;
}

function InvitationsTabContentWrapper() {
  const { selectedPractice } = usePractice();

  if (!selectedPractice) {
    return (
      <Container disableGutters>
        <Typography variant="body1">
          Please select an organization to view its invitations
        </Typography>
      </Container>
    );
  }

  return <InvitationsTabContent organizationId={selectedPractice.id} organization={selectedPractice} />;
}

export default function Practice() {
  const { selectedPractice } = usePractice();
  const { tab } = useParams();
  const navigate = useNavigate();
  const isLab = Boolean(selectedPractice?.is_lab);

  const tabs = React.useMemo(
    () => [
      { label: 'Details', route: null },
      { label: 'Transactions', route: 'transactions' },
      { label: "Org's Users", route: 'users' },
      { label: 'Invitations', route: 'invitations' },
    ],
    []
  );

  const getTabIndex = React.useCallback(() => {
    if (!tab) return 0;
    const idx = tabs.findIndex((t) => t.route === tab);
    return idx >= 0 ? idx : 0;
  }, [tab, tabs]);

  const [tabIndex, setTabIndex] = React.useState(getTabIndex());
  const safeTabIndex = Math.min(tabIndex, Math.max(tabs.length - 1, 0));

  React.useEffect(() => {
    setTabIndex(getTabIndex());
  }, [tab, getTabIndex]);

  const handleTabChange = (_, newValue) => {
    setTabIndex(newValue);
    const target = tabs[newValue];
    navigate(target?.route ? `/practice/${target.route}` : '/practice');
  };

  if (isLab) {
    return <Navigate to={tab ? `/lab/${tab}` : '/lab'} replace />;
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <OrganizationWorkspaceHeader title="Practice" />

      {!selectedPractice && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a practice to view details.
        </Typography>
      )}

      {!selectedPractice ? (
        <OrganizationEmptyState label="practice" />
      ) : (
        <>
          <Tabs value={safeTabIndex} onChange={handleTabChange} sx={{ mb: 3 }}>
            {tabs.map((t) => (
              <Tab key={t.label} label={t.label} />
            ))}
          </Tabs>
          {safeTabIndex === 0 && <PracticeDetails />}
          {safeTabIndex === 1 && <TransactionsTabContent />}
          {safeTabIndex === 2 && <UsersTabContentWrapper />}
          {safeTabIndex === 3 && <InvitationsTabContentWrapper />}
        </>
      )}
    </Container>
  );
}
